import { PaymentDueBanner as SharedPaymentDueBanner } from "@ews-admin/global-design-system";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectLicenceState } from "../../store/reducers/licenceSlice";

function getDaysUntil(dateStr: string): number {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(dateStr);
  due.setHours(0, 0, 0, 0);
  return Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

export const PaymentDueBanner: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { licence } = useSelector(selectLicenceState);
  const [dismissed, setDismissed] = useState(false);

  // A licence that has never been paid is first-time onboarding, not an overdue
  // renewal — regardless of its status. Admin-onboarded managers get an ACTIVE
  // licence with a payment_due_date but have never paid (last_payment_date is
  // only set on the first successful payment, in the API's PaymentService), so
  // they must not see an overdue / "risk of suspension" warning. The licence
  // page guides them to pay their first invoice instead.
  const neverPaid = !licence?.lastPaymentDate;

  if (dismissed || !licence?.paymentDueDate || neverPaid) return null;

  const daysLeft = getDaysUntil(licence.paymentDueDate);

  if (daysLeft > 7) return null;

  const isOverdue = daysLeft < 0;

  const title = isOverdue
    ? t("banner.payment.overdueTitle", { days: Math.abs(daysLeft) })
    : daysLeft === 0
      ? t("banner.payment.dueTodayTitle")
      : t("banner.payment.dueSoonTitle", { days: daysLeft });

  const subtitle = isOverdue
    ? t("banner.payment.overdueSubtitle")
    : t("banner.payment.dueSoonSubtitle");

  const warning =
    !isOverdue && licence.discountLost === false
      ? t("banner.payment.discountWarning")
      : undefined;

  return (
    <SharedPaymentDueBanner
      title={title}
      subtitle={subtitle}
      warning={warning}
      ctaLabel={t("banner.payment.cta")}
      onCtaClick={() => navigate("/manager/licence")}
      onDismiss={() => setDismissed(true)}
      dismissLabel={t("common.close")}
    />
  );
};
